import type { RoomTemplate } from '@brush/api-client';
import { listPhrase, missingRequiredColumns } from './columns';
import { missingRequiredResultColumns } from './resultColumns';

/**
 * Which of the room's templates can actually import a file today.
 *
 * Three things have to hold, and each fails for a reason the room can act on, so an unusable
 * template is listed with its reason rather than hidden: a template that vanishes from a picker
 * looks like a platform problem, when in every one of these cases it is work waiting on the room.
 */

export const IMPORT_PURPOSE = 'import_tournaments';
export const RESULTS_IMPORT_PURPOSE = 'import_tournament_results';

export interface ImportCandidate {
  entry: RoomTemplate;
  /** The room's columns, in its order — non-null exactly when the template can be used. */
  fieldKeys: string[] | null;
  /** Why it can't be used, in the room's own terms. */
  blockedBy: string | null;
}

/** What a missing required column looks like, whichever vocabulary was consulted. */
interface NamedColumn {
  label: string;
}

/**
 * The gate itself, over whichever purpose and vocabulary is being asked about. Shared so the two
 * importers cannot drift on *why* a template is unusable — the reasons are about the template and
 * the room's content, not about what the file describes.
 */
function candidatesFor(
  entries: readonly RoomTemplate[],
  purpose: string,
  missingRequired: (fieldKeys: readonly string[]) => NamedColumn[],
): ImportCandidate[] {
  return entries
    .filter((entry) => entry.template.purpose === purpose)
    .map<ImportCandidate>((entry) => {
      const { content } = entry;

      // Locked rule: a template a room has not filled in cannot be used by that room. There is no
      // platform-level content to fall back on.
      if (!content) {
        return { entry, fieldKeys: null, blockedBy: "You haven't filled this template in yet." };
      }

      // Not a stale flag — the platform can add a required field at any time, which invalidates
      // content saved before it without touching that content.
      if (!content.isComplete) {
        return {
          entry,
          fieldKeys: null,
          blockedBy: 'This template gained a required field since you filled it in.',
        };
      }

      const missing = missingRequired(content.fieldKeys);
      if (missing.length > 0) {
        return {
          entry,
          fieldKeys: null,
          blockedBy: `Your columns for it don't include ${listPhrase(
            missing.map((m) => m.label),
          )}.`,
        };
      }

      return { entry, fieldKeys: content.fieldKeys, blockedBy: null };
    })
    .sort(
      (a, b) =>
        Number(a.blockedBy !== null) - Number(b.blockedBy !== null) ||
        a.entry.template.name.localeCompare(b.entry.template.name),
    );
}

/** Templates that can import a file of tournaments. */
export const importCandidates = (entries: readonly RoomTemplate[]): ImportCandidate[] =>
  candidatesFor(entries, IMPORT_PURPOSE, missingRequiredColumns);

/** Templates that can import a file of finishing positions. */
export const resultImportCandidates = (entries: readonly RoomTemplate[]): ImportCandidate[] =>
  candidatesFor(entries, RESULTS_IMPORT_PURPOSE, missingRequiredResultColumns);

export const usable = (candidates: readonly ImportCandidate[]): ImportCandidate[] =>
  candidates.filter((c) => c.fieldKeys !== null);
